import type { FC } from "react";

import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import type { Product } from "@jti/magento";
import { useSelector } from "@jti/store/reduxHooks";
import { selectProductFreeGiftsInCart } from "@jti/store/selectors/selectProductFreeGiftsInCart";
import { Dialog, Image, Price, Txt, useTranslations } from "@jti/ui";

import { SummaryCartItemsGiftBadge } from "../SummaryCartItems/SummaryCartItemsGiftsBadge";

import styles from "./RemoveItemDialog.module.scss";

const { sanitize } = dompurify;

type RemoveItemDialogProps = {
  item: Product;
  isOpenDialog: boolean;
  onRemove(): void;
  onCancel(): void;
};

export const RemoveItemDialog: FC<RemoveItemDialogProps> = ({ isOpenDialog, onCancel, onRemove, item }) => {
  const { __ } = useTranslations();
  const { showPricesWithTaxExcluded = false } = useConfig();

  const freeGifts = useSelector((state) =>
    selectProductFreeGiftsInCart(state, item.id, false, showPricesWithTaxExcluded)
  );

  return (
    <Dialog className={styles.dialog} isOpened={isOpenDialog} onClose={onCancel}>
      <div className={styles.title}>{__("cart.items.remove_dialog.title")}</div>
      <div className={styles.cardItem} data-testid={"remove-item-dialog"}>
        <Card variant="level-2" key={item.name}>
          <div className={styles.container}>
            <div className={styles.image}>
              <Image url={item.imageUrl} />
            </div>
            <div>
              <Txt className={styles.name}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitize(item.name),
                  }}
                />
              </Txt>
              <Txt className={styles.description}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitize(item.description),
                  }}
                />
              </Txt>
              <Price price={item.price} className={styles.price} />
            </div>
          </div>
          {freeGifts.map((giftItem) => (
            <div className={styles.container} key={giftItem.id}>
              <div className={styles.image}>
                <Image url={giftItem.imageUrl} />
              </div>
              <div>
                <Txt className={styles.name}>
                  <span>{`${giftItem.quantity}× ${giftItem.name}`}</span>
                </Txt>
                <SummaryCartItemsGiftBadge />
                <Price
                  price={{
                    ...giftItem.price,
                    value: 0,
                  }}
                  className={styles.price}
                />
              </div>
            </div>
          ))}
        </Card>
      </div>
      <div className={styles.btnGroup}>
        <Button data-testid="remove-item-submit-button" className={styles.button} variant="primary" onClick={onRemove}>
          {__("cart.items.remove_dialog.save_button_label")}
        </Button>
        <Button
          data-testid="remove-item-cancel-button"
          className={styles.button}
          variant="secondary"
          onClick={onCancel}
        >
          {__("cart.items.remove_dialog.cancel_button_label")}
        </Button>
      </div>
    </Dialog>
  );
};
